import { Injectable } from '@angular/core';

type CacheEntry<T> = { [key: string]: T };

@Injectable({ providedIn: 'root' })
export class SessionCacheService<T extends object = object> {
	/** Cache user selections and active temporary state changes */
	private temporaryCache = new Map<string, CacheEntry<T>>();
	/** Cache general session state */
	private sessionCache = new Map<string, CacheEntry<T>>();

	/**
	 * Gets the temporary cache.
	 * @param entityId id of the map type you want, i.e. "feature-coverage-map",
	 * "feature-routing" etc.
	 * @param featureId the id of the room/device etc.
	 * @returns T or undefined
	 */
	public getTempCache(entityId: string, featureId: string): T | undefined {
		return this.temporaryCache.get(entityId)?.[featureId];
	}

	/**
	 * Gets the temporary cache. If temporary cache is not set, then gets the session cache.
	 * @param entityId id of the map type you want, i.e. "feature-coverage-map",
	 * "feature-routing" etc.
	 * @param featureId the id of the room/device etc.
	 * @returns T or undefined
	 */
	public getTempCacheOrSessionCache(entityId: string, featureId: string): T | undefined {
		const tempCachedFeature: T | undefined = this.getTempCache(entityId, featureId);
		if (tempCachedFeature) {
			return tempCachedFeature;
		}

		return this.sessionCache.get(entityId)?.[featureId];
	}

	/**
	 * Store the temporary cache, which can be deleted when not needed anymore.
	 * @param entityId is the the map-key, to which the view/feature belongs to,
	 * for example for coverage view, it is 'feature-coverage-map'
	 * @param featureId is expected to be a key to a dictionary i.e. roomId/deviceId etc,
	 * @param data the value want to store i.e. CoverageMapState, RoutingState etc.
	 */
	public storeTempCache(entityId: string, featureId: string, data: T): void {
		const cacheEntry = this.temporaryCache.get(entityId);

		if (cacheEntry) {
			cacheEntry[featureId] = data;
		} else {
			this.temporaryCache.set(entityId, { [featureId]: data });
		}
	}

	/**
	 * Store the session cache, which stays active.
	 * @param entityId is the the map-key, to which the view/feature belongs to,
	 * for example for coverage view, it is 'feature-coverage-map'
	 * @param featureId is expected to be a key to a dictionary i.e. roomId/deviceId etc,
	 * @param data the value want to store i.e. CoverageMapState, RoutingState etc.
	 */
	public storeSessionCache(entityId: string, featureId: string, data: T): void {
		const cacheEntry = this.sessionCache.get(entityId);

		if (cacheEntry) {
			cacheEntry[featureId] = data;
		} else {
			this.sessionCache.set(entityId, { [featureId]: data });
		}
	}

	/**
	 * Delete only the temporary cache.
	 * @param featureId the roomId or deviceId etc, to delete data for.
	 * All data for the key will be deleted.
	 */
	public deleteTempCache(featureId: string): void {
		//go thru each session type(i.e. feature-coverage-map, feature-routing,  and so on)
		this.temporaryCache.forEach((entity) => {
			//within that type, delete that key(roomid, deviceid etc)
			delete entity[featureId];
		});
	}
}
